import { useCallback, useEffect, useState } from "react";
import useAuth from "./useAuth";
import { dashboardService } from "../services/dashboard.service";
import { onDashboardRefresh } from "../utils/dashboardEvents";

const EMPTY_STATS = {
  cases: 0,
  pendingConfirmations: 0,
  confirmations: 0,
  inventoryConfirmed: 0,
};

export default function useDashboardStats() {
  const { auth } = useAuth();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboardStats = useCallback(async () => {
    if (!auth?.token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const results = await Promise.allSettled([
        dashboardService.getStatsOverview(),
        dashboardService.getPendingConfirmationsCount(),
        dashboardService.getConfirmationsCount(),
        dashboardService.getInventoryConfirmedCount(),
      ]);

      const pick = (idx, fallback = 0) => {
        const r = results[idx];
        if (r.status !== "fulfilled") return { value: fallback, failed: r.reason };
        return { value: r.value, failed: null };
      };

      const overview = pick(0);
      const pending = pick(1);
      const confirmations = pick(2);
      const inventory = pick(3);

      const checks = [overview, pending, confirmations, inventory];
      const firstForbidden = checks.find((x) => x.failed?.response?.status === 403);
      const pendingFailed = pending.failed;
      const pendingMsg =
        pendingFailed?.response?.data?.message || pendingFailed?.message;

      if (firstForbidden?.failed) {
        setError(
          firstForbidden.failed.response?.data?.message ||
            "Forbidden — restart the backend server, then log out and log in again."
        );
      } else if (pendingFailed) {
        setError(
          pendingMsg ||
            "Could not load pending confirmations. Restart the backend (port 5001) and refresh."
        );
      } else {
        setError("");
      }

      setStats({
        cases: overview?.value?.data?.cases ?? 0,
        pendingConfirmations: pending.value?.data?.pendingConfirmations ?? 0,
        confirmations: confirmations.value?.data?.totalConfirmations ?? 0,
        inventoryConfirmed: inventory.value?.data?.inventoryConfirmed ?? 0,
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load dashboard stats");
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  useEffect(() => {
    return onDashboardRefresh(loadDashboardStats);
  }, [loadDashboardStats]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        loadDashboardStats();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loadDashboardStats]);

  return { stats, loading, error, reload: loadDashboardStats };
}
